import React, {useEffect, useRef, useState} from 'react';
import {Accordion, AccordionDetails, AccordionSummary, Grid, Typography, useTheme} from "@mui/material";
import {useFormContext} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {ExpandMore} from "@mui/icons-material";
import {FormAutocomplete, FormTextField} from '../../../common/form-fields';
import {ExcessPlanDetailResponse, ExcessPlanResponse, FormProductPlan} from '../../../api/types/productPlan';
import {useRecoilState} from "recoil";
import {excessPlanAtom} from "../../../state/productPlan";
import {useProductPlanRepository} from "../../../api/repositories/ProductPlanRepository";
import AgGridCustomTable from '../../../../../util/component/AgGridCustomTable';
import {dateFormatterShort} from '../../../utils';
import {AgGridReact} from 'ag-grid-react';
import {useAgGridLocale} from '../../../../../config/globalConfig';

const ProductPlanException = () => {
    // Use State
    const [excessExpanded, setExcessExpanded] = useState<boolean>(false);
    const [carGroups, setCarGroups] = useState<string[]>([])
    const [excessMatrix, setExcessMatrix] = useState<ExcessPlanDetailResponse[]>([]);
    const [excessDetail, setExcessDetail] = useState([{}])

    // Use Recoil
    const [excessPlan] = useRecoilState(excessPlanAtom)

    // Use Theme
    const theme = useTheme();

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Form Context
    const {watch, setValue, register} = useFormContext<FormProductPlan>();

    // Use Custom Hook
    const {getExcessGroupDetail} = useProductPlanRepository();

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    useEffect(() => {
        const populateExceptionFields = () => {
            setValue("productPlan.excessPlan.name", (excessPlan as ExcessPlanResponse).excessGroup?.name)
            setValue("productPlan.excessPlan.from", dateFormatterShort((excessPlan as ExcessPlanResponse).excessGroup?.validityDateFrom))
            setValue("productPlan.excessPlan.to", dateFormatterShort((excessPlan as ExcessPlanResponse).excessGroup?.validityDateTo))
            setCarGroups((excessPlan as ExcessPlanResponse).carGroups)
        }

        if (excessPlan) {
            populateExceptionFields()
        }
    }, [excessPlan])

    const getExcessDetail = async (value: string) => {
        setValue("productPlan.excessPlan.carGroup", value)
        const formValues = watch();
        const excessDetail: ExcessPlanDetailResponse[] = await getExcessGroupDetail(excessPlan?.excessGroup.id as number, formValues.productPlan.productCode?.code as string, formValues.productPlan.locationGroup?.code as number, formValues.productPlan.excessPlan.carGroup as string)
        setExcessMatrix(excessDetail)
        setExcessDetail([{
            carGroups: excessDetail.map(el => el.carGroup),
            amount: excessDetail.map(el => el.amount)
        }])
    }

    const columnDefs = [
        {
            headerName: t("pricing:excessGroup.table.headers.excesses"),
            headerClass: ["bordersRight"],
            children:
                excessMatrix.map((ex) => {
                    return {
                        headerName: ex?.excess.name,
                        headerClass: ["bordersRight"],
                        field: ex?.excess.name,
                        width: excessMatrix?.length > 6 ? 100 : undefined,
                        flex: excessMatrix?.length > 6 ? undefined : 1,
                        cellEditor: "customEditor",
                        cellRenderer: () => ex?.amount / 100,
                        cellStyle: {
                            justifyContent: "flex-end",
                            textAlign: "right",
                            padding: 5,
                            borderRight: "2px solid #E0E0E0"
                        },
                    };
                })
        },
    ];

    return (
        <Accordion
            expanded={excessExpanded}
            onChange={(e, expanded) => setExcessExpanded(expanded)}
        >
            <AccordionSummary expandIcon={<ExpandMore/>}>
                <Typography sx={{color: theme.palette.primary.dark}}>
                    {"EXCESSES PLAN"}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container rowSpacing={2} columnSpacing={2}>
                    <Grid item xs={12} md={6}>
                        {/* COMBINATION */}
                        <FormTextField
                            {...register("productPlan.excessPlan.name")}
                            label={`${t("pricing:productPlan.combinationName")}`}
                            type={"text"}
                            readOnly={true}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* CAR GROUPS */}
                        <FormAutocomplete
                            {...register("productPlan.excessPlan.carGroup")}
                            label={`${t("pricing:excessGroup.form.labels.carGroup")}*`}
                            options={carGroups}
                            isOptionEqualToValue={(
                                option: string,
                                value: string
                            ) => option === value}
                            handleChange={getExcessDetail}
                            readOnly={!excessPlan}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        {/* FROM */}
                        <FormTextField
                            {...register("productPlan.excessPlan.from")}
                            label={`${t("pricing:ancillary.form.labels.reservationFrom")}`}
                            type={"text"}
                            readOnly={true}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        {/* TO */}
                        <FormTextField
                            {...register("productPlan.excessPlan.to")}
                            label={`${t("pricing:ancillary.form.labels.reservationTo")}`}
                            type={"text"}
                            readOnly={true}
                        />
                    </Grid>
                    {watch("productPlan.excessPlan.carGroup") &&
                        <Grid item xs={12}>
                            <AgGridCustomTable
                                gridRef={gridRef}
                                columnDefs={columnDefs}
                                localeText={agGridLocale}
                                tooltipShowDelay={0}
                                stopEditingWhenCellsLoseFocus
                                rowData={excessDetail}
                            >
                            </AgGridCustomTable>
                        </Grid>
                    }
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}

export default ProductPlanException;